/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";

type Simplify<T> = { [K in keyof T]: T[K] } & {};

// Make the keys present in D optional on P
type Defaultize<P, D extends Partial<P>> = Simplify<Omit<P, keyof D> & Partial<Pick<P, Extract<keyof D, keyof P>>>>;

/**
 * withDefaults
 * Wrap a component with default prop values.
 *
 * - Any keys present in `defaults` become optional for consumers.
 * - Forwards refs correctly for both function and class components.
 * - Keeps a helpful displayName for React DevTools.
 */
export function withDefaults<
  C extends React.ComponentType<any>,
  // Defaults should only apply to the component's own props (no ref)
  D extends Partial<React.ComponentPropsWithoutRef<C>>,
>(Component: C, defaults: D) {
  type OwnProps = React.ComponentPropsWithoutRef<C>;
  type P = React.ComponentPropsWithRef<C>;
  type R = React.ComponentRef<C>;
  // Expose own props with defaults applied (forwardRef will add the ref prop itself)
  type PublicProps = Defaultize<OwnProps, D>;

  const Wrapped = React.forwardRef<R, PublicProps>((props, ref) => {
    // Merge defaults first so `props` takes precedence
    const merged = { ...defaults, ...props } as unknown as P;
    // Use createElement to avoid JSX generic assignability issues
    return React.createElement(Component as unknown as React.ComponentType<any>, {
      ...(merged as unknown as object),
      ref: ref as any,
    });
  });

  Wrapped.displayName = `withDefaults(${Component.displayName || Component.name || "Component"})`;

  // Optional: memo to avoid unnecessary re-renders when props are shallow-equal
  return React.memo(Wrapped);
}
